import type { NodeDef, RecipeDef, StageDef } from "@reagent/sim";
import { freshCardInstances } from "@reagent/sim";
import { ALL_CARDS } from "../../cards";
import { pollutionCardsFor, reachablePollutionReasons } from "../../pollution";
import { setupOfferFor, validateStage } from "../../schema";
import type { StageSolution } from "../../solutions";
import { MT_CEMENT_BUFFER, MT_SMELTER_BUFFER, MT_SMELTER_CRITICAL_TEMP } from "./machines";
import { ERA_1_SCALARS } from "./shared";

/**
 * Copper & orichalcum (MT), Era 1's capacity pathway (`06` section 4.3).
 *
 * The core smelts ore at a high `k`, so the reactor makes heat faster than any
 * single jacket can shed it; the refined tier cements the copper into brass
 * behind a temperature-gated hold. Every number below is a PLACEHOLDER
 * (spec 0037): chosen so the worked solutions clear, needing playtest.
 */

const card = (id: string) => ALL_CARDS.find((c) => c.id === id)!;

// PLACEHOLDER (spec 0037): needs playtest. Recipe ratios, heat, hold and gate.
// - Smelt: k is set by the feeds (3 multiples a tick), so heat per tick is
//   3 x heat 1; a twin jacket sheds 2, leaving +1 a tick for Flash Freeze to reset.
// - Cementation: `Copper:a` with a = 2 so the reactor has to pool more than one
//   multiple; four multiples a tick keeps Sludge a small share of the product.
// - Cementation hold: a single heater takes a product from ambient 20 to 50.
export const MT_SMELT: RecipeDef = {
  id: "mt-smelt",
  inputs: { Ore: 1, Fuel: 2, Lime: 1 },
  outputs: { Copper: 1, Oxide: 1 },
  isExothermic: true,
  heat: 1,
  nodeKinds: ["reactor"],
};

export const MT_TAP: RecipeDef = {
  id: "mt-tap-split",
  inputs: { Copper: 1, Oxide: 1 },
  outputs: { Copper: 1, Oxide: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["separator"],
  primarySpecies: ["Copper"],
};

export const MT_CEMENTATION: RecipeDef = {
  id: "mt-cementation",
  inputs: { Copper: 2, Calamine: 1, Fuel: 1 },
  outputs: { Charge: 4 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["reactor"],
};

export const MT_CEMENTATION_HOLD: RecipeDef = {
  id: "mt-cementation-hold",
  inputs: { Charge: 1 },
  outputs: { Brass: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["incubator"],
  holdDuration: 2,
  minTemperature: 50,
};

/** Every recipe this pathway authors; the orchestrator adds them to the shared catalogue. */
export const MT_RECIPES: readonly RecipeDef[] = [MT_SMELT, MT_TAP, MT_CEMENTATION, MT_CEMENTATION_HOLD];

// PLACEHOLDER (spec 0037): needs playtest. Contract prices and impurity units.
// Budget is always cheaper than Premium; Sludge is the conventional impurity.
const sourcing = (budgetCost: number, premiumCost: number) => ({
  budget: { impurities: { Sludge: 1 }, costCredits: budgetCost },
  premium: { costCredits: premiumCost },
});

/** Facility Mousetrap's default Phase 1 price from spec 0028 (needs playtest). */
const MOUSETRAP_OFFER = { cardId: "card-facility-mousetrap", costCredits: 15 } as const;

/**
 * Builds a module, mapping every reachable pollution reason rather than
 * hand-listing them: `validateStage` demands each one, and this stays right if
 * the board changes.
 */
function finishStage(bare: StageDef): StageDef {
  return validateStage({ ...bare, pollutionCards: pollutionCardsFor(...reachablePollutionReasons(bare, ALL_CARDS)) }, ALL_CARDS);
}

// ---- Core ---------------------------------------------------------------

// PLACEHOLDER (spec 0037): needs playtest. Feed sizes, the smelter's stats and the quota.
// The smelter is pre-placed with the Heavy-Duty Mixer's own stats (criticalTemp 8,
// buffer 4): it must hold three arrivals plus last tick's product, and no
// catalogue card sells a bigger reactor. The three feed pipes are all four steps
// long, so the feeds land on the same tick and no surplus rides into the slag.
const CORE_NODES: readonly NodeDef[] = [
  { id: "mt-ore", kind: "inputTank", pos: { x: 0, y: 0 }, emits: { Ore: 3 }, sourcing: sourcing(4, 9) },
  { id: "mt-fuel", kind: "inputTank", pos: { x: 0, y: 2 }, emits: { Fuel: 6 }, sourcing: sourcing(3, 6) },
  { id: "mt-lime", kind: "inputTank", pos: { x: 0, y: 4 }, emits: { Lime: 3 }, sourcing: sourcing(3, 6) },
  {
    id: "mt-smelter",
    kind: "reactor",
    pos: { x: 2, y: 2 },
    criticalTemp: MT_SMELTER_CRITICAL_TEMP,
    bufferCapacity: MT_SMELTER_BUFFER,
    recipeIds: ["mt-smelt"],
  },
  { id: "mt-tap", kind: "separator", pos: { x: 3, y: 2 }, recipeIds: ["mt-tap-split"] },
  { id: "mt-out", kind: "productHopper", pos: { x: 5, y: 1 }, bufferCapacity: 99 },
  { id: "mt-slag", kind: "wasteBuyer", pos: { x: 5, y: 3 } },
];

export const MT_CORE: StageDef = finishStage({
  id: "era1-mt-core",
  name: "Copper Smelt",
  blurb: "Charcoal, lime and ore make copper faster than one jacket can cool it. Tap the slag off for the glassmakers.",
  cols: 6,
  rows: 5,
  nodes: [...CORE_NODES],
  edges: [
    {
      id: "mt-e-ore",
      fromNodeId: "mt-ore",
      toNodeId: "mt-smelter",
      path: [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 0 }, { x: 2, y: 1 }, { x: 2, y: 2 }],
    },
    {
      id: "mt-e-fuel",
      fromNodeId: "mt-fuel",
      toNodeId: "mt-smelter",
      path: [{ x: 0, y: 2 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 2, y: 2 }],
    },
    {
      id: "mt-e-lime",
      fromNodeId: "mt-lime",
      toNodeId: "mt-smelter",
      path: [{ x: 0, y: 4 }, { x: 1, y: 4 }, { x: 2, y: 4 }, { x: 2, y: 3 }, { x: 2, y: 2 }],
    },
    { id: "mt-e-smelt", fromNodeId: "mt-smelter", toNodeId: "mt-tap", path: [{ x: 2, y: 2 }, { x: 3, y: 2 }] },
    {
      id: "mt-e-copper",
      fromNodeId: "mt-tap",
      toNodeId: "mt-out",
      separationRole: "main",
      path: [{ x: 3, y: 2 }, { x: 4, y: 2 }, { x: 5, y: 2 }, { x: 5, y: 1 }],
    },
    {
      id: "mt-e-slag",
      fromNodeId: "mt-tap",
      toNodeId: "mt-slag",
      separationRole: "waste",
      path: [{ x: 3, y: 2 }, { x: 3, y: 3 }, { x: 4, y: 3 }, { x: 5, y: 3 }],
    },
  ],
  recipes: [MT_SMELT, MT_TAP],
  objective: { targetComposition: { Copper: 24 }, minPurity: ERA_1_SCALARS.minPurity },
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances([
    "card-cooling-jacket",
    "card-cooling-jacket-twin",
    "card-flash-freeze",
    "card-solvent-flush",
    "card-rush-order",
    "card-overtime",
  ]),
  // PLACEHOLDER (spec 0037): needs playtest. Covers the three Budget contracts with every
  // commodity price doubled (16) plus the Mousetrap, with a little slack.
  setupBudgetCredits: 35,
  setupCatalogue: [
    setupOfferFor(card("card-mt-smelter")),
    setupOfferFor(card("card-mixer")),
    setupOfferFor(card("card-mixer-heavy")),
    MOUSETRAP_OFFER,
  ],
});

export const MT_CORE_SOLUTION: StageSolution = {
  stageId: MT_CORE.id,
  sourcing: { "mt-ore": "budget", "mt-fuel": "budget", "mt-lime": "budget" },
  // Bought in every plan: the package has no lid, and paired with another unlidded buyer (GF x MT) the slag midden decides the stage.
  setupBuild: [{ cardId: "card-facility-mousetrap" }],
  turns: [
    [{ cardId: "card-cooling-jacket-twin", at: { x: 2, y: 2 } }],
    [{ cardId: "card-flash-freeze" }],
    [{ cardId: "card-flash-freeze" }],
    [{ cardId: "card-flash-freeze" }],
  ],
  notes:
    "The smelter is pre-placed and fires at k = 3, adding 3 heat a tick against a criticalTemp of 8. The twin " +
    "jacket sheds 2 of it, which alone lets the counter creep to critical and then explode, so a Flash Freeze at " +
    "the start of every following turn resets it before the warning can run its two ticks. Play the jacket on " +
    "turn 1; the smelt starts on tick 7. Budget on all three feeds: the Sludge only ever rides into the slag.",
};

// ---- Refined ------------------------------------------------------------

// PLACEHOLDER (spec 0037): needs playtest. Feed sizes and the quota. Calamine is
// external-only and unsourced (06 section 4.6 lists no Budget contract for it).
export const MT_REFINED: StageDef = finishStage({
  id: "era1-mt-refined",
  name: "Cementation",
  blurb: "Copper, calamine and charcoal, heated and held until they turn to brass.",
  cols: 6,
  rows: 5,
  nodes: [
    { id: "mt-r-copper", kind: "inputTank", pos: { x: 0, y: 0 }, emits: { Copper: 8 }, sourcing: sourcing(5, 10) },
    { id: "mt-r-calamine", kind: "inputTank", pos: { x: 0, y: 2 }, emits: { Calamine: 4 } },
    { id: "mt-r-fuel", kind: "inputTank", pos: { x: 0, y: 4 }, emits: { Fuel: 4 }, sourcing: sourcing(3, 6) },
    { id: "mt-r-cement", kind: "reactor", pos: { x: 2, y: 2 }, bufferCapacity: MT_CEMENT_BUFFER, recipeIds: ["mt-cementation"] },
    { id: "mt-r-out", kind: "productHopper", pos: { x: 5, y: 2 }, bufferCapacity: 99 },
  ],
  edges: [
    {
      id: "mt-r-e-copper",
      fromNodeId: "mt-r-copper",
      toNodeId: "mt-r-cement",
      path: [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 0 }, { x: 2, y: 1 }, { x: 2, y: 2 }],
    },
    {
      id: "mt-r-e-calamine",
      fromNodeId: "mt-r-calamine",
      toNodeId: "mt-r-cement",
      path: [{ x: 0, y: 2 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 2, y: 2 }],
    },
    {
      id: "mt-r-e-fuel",
      fromNodeId: "mt-r-fuel",
      toNodeId: "mt-r-cement",
      path: [{ x: 0, y: 4 }, { x: 1, y: 4 }, { x: 2, y: 4 }, { x: 2, y: 3 }, { x: 2, y: 2 }],
    },
  ],
  recipes: [MT_CEMENTATION, MT_CEMENTATION_HOLD],
  objective: { targetComposition: { Brass: 48 }, minPurity: ERA_1_SCALARS.minPurity },
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances(["card-solvent-flush", "card-rush-order", "card-overtime"]),
  // PLACEHOLDER (spec 0037): needs playtest. Heater 10 + incubator 15 + three pipes 15,
  // plus the Copper and Fuel contracts with Fuel's price doubled (11), with a little slack.
  setupBudgetCredits: 55,
  setupCatalogue: [
    setupOfferFor(card("card-mt-cementation")),
    setupOfferFor(card("card-mixer")),
    setupOfferFor(card("card-mixer-heavy")),
    setupOfferFor(card("card-heater")),
    setupOfferFor(card("card-incubator")),
    setupOfferFor(card("card-pipe-glass")),
  ],
});

export const MT_REFINED_SOLUTION: StageSolution = {
  stageId: MT_REFINED.id,
  sourcing: { "mt-r-copper": "budget", "mt-r-fuel": "budget" },
  setupBuild: [
    { cardId: "card-heater", at: { x: 3, y: 2 } }, // mints n1
    { cardId: "card-incubator", at: { x: 4, y: 2 } }, // mints n2
    { cardId: "card-pipe-glass", fromNodeId: "mt-r-cement", toNodeId: "n1" },
    { cardId: "card-pipe-glass", fromNodeId: "n1", toNodeId: "n2" },
    { cardId: "card-pipe-glass", fromNodeId: "n2", toNodeId: "mt-r-out" },
  ],
  turns: [],
  notes:
    "Nothing is played: the whole answer is the Setup build. The heater is what lifts the charge from the reactor's " +
    "ambient 20 to the hold's 50 gate; without it the incubator lets the charge straight through and no brass is made.",
};
